import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import Dropdown from "../../components/common/Dropdown";
import { getTrackIds } from "../../api/administration";
import { TrackIds, TrackListenedHistory } from "../../types/types";
import { getTrackListenedStoriesByDate } from "../../api/support";
import { DatePicker } from "@mui/x-date-pickers";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../components/common/FormFields";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import studio from "../../styles/studioPage.module.css";

const TrackListenedStories: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [selectedTrack, setSelectedTrack] = React.useState<string>("");
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [tracks, setTracks] = React.useState<TrackIds[]>([]);
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [endDate, setEndDate] = React.useState<Date | null>(null);

    const [trackListenedStories, setTrackListenedStories] = React.useState<TrackListenedHistory | null>(null);

    const fetchTrackIds = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getTrackIds(token);
            setTracks(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrackIds();
    }, []);

    const tracksForDropdown = tracks.map((track) => ({
        label: track.name,
        value: track.id,
    }));

    const getTrackListenedStories = async () => {
        if (!selectedTrack || !startDate || !endDate) {
            setError(t("selectAllFields"));
            return;
        } else {
            setError(null);
        }
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getTrackListenedStoriesByDate(token, selectedTrack, startDate, endDate);
            setTrackListenedStories(response);
        } catch (error) {
            console.error("Failed to fetch track listened stories", error);
            setError(t("fetchError"));
        } finally {
            setLoading(false);
        }
    };

    const rows = trackListenedStories || [];

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("trackListenedStories")} subtitle={t("trackListenedStoriesSubTitle")} />
            </div>

            <div className={`${adminSurface.supportFilterBar} ${adminSurface.trackListenedSection}`}>
                <div className={adminSurface.supportSelFl}>
                    <label>{t("selectProject")}</label>
                    <Dropdown
                        label=""
                        value={selectedTrack}
                        options={tracksForDropdown}
                        onChange={(value) => setSelectedTrack(value)}
                        placeholder={t("selectProject")}
                        loading={loading}
                        error={!!error}
                        isWithoutMargin={true}
                    />
                </div>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <ThemeProvider theme={theme}>
                        <div className={adminSurface.supportDateFl}>
                            <label>{t("startDate")}</label>
                            <DatePicker value={startDate} onChange={(e) => setStartDate(e)} />
                        </div>
                        <div className={adminSurface.supportDateFl}>
                            <label>{t("endDate")}</label>
                            <DatePicker value={endDate} onChange={(e) => setEndDate(e)} />
                        </div>
                    </ThemeProvider>
                </LocalizationProvider>

                <button
                    className={adminSurface.addButton}
                    onClick={getTrackListenedStories}
                    disabled={loading || !selectedTrack || !startDate || !endDate}
                >
                    {loading ? t("loading") : t("submit")}
                </button>
            </div>

            <h2 className={adminSurface.supportRowsTitle}>{t("listenedStories")}</h2>
            <div
                className={`${adminSurface.tableContainer} ${adminSurface.tblScrollSupport} ${adminSurface.trackListenedStoriesContainer}`}
            >
                <table className={adminSurface.table}>
                    <thead>
                        <tr>
                            <th>{t("trackName")}</th>
                            <th>{t("waypointName")}</th>
                            <th>{t("position")}</th>
                            <th>{t("listenedAt")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={4} className={adminSurface.supportEmpty}>
                                    {error || t("noItemsFound") || "—"}
                                </td>
                            </tr>
                        ) : (
                            rows.map((story) => (
                                <tr key={story.createdAt}>
                                    <td>{story.trackName}</td>
                                    <td>{story.waypointName}</td>
                                    <td>{story.position}</td>
                                    <td>{new Date(story.createdAt).toLocaleString()}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TrackListenedStories;
